import { action, makeObservable, observable, runInAction } from 'mobx';
import apiService from './apiService.ts';
import { socketService } from './socketService.ts';
import { toastService } from '../components/Toast.tsx';
import { modalService } from '../components/Modal.tsx';
import type { BackgroundProgressData, BackgroundDoneData, BackgroundErrorData } from './socketService.ts';

export type UploadItem = {
  id: string;
  fileName: string;
  fileSize: number;
  loaded: number;
  progress: number;         // 0-100
  status: 'registering' | 'uploading' | 'processing' | 'done' | 'error';
  error?: string;
  bitrate?: number;         // bytes/sec
  timeLeftSeconds?: number;
  parentFolderId?: string | null;
  abortController?: AbortController;
};

type IncompleteFileInfo = {
  id: string;
  fileName: string;
  fileSize: number;
  readySize: number;
};

const CHUNK_SIZE = 1024 * 1024; // 1 MB chunks

class UploadService {
  @observable uploads: UploadItem[] = [];
  
  private _onCompleteCallbacks: (() => void)[] = [];

  onUploadComplete(callback: () => void) {
    this._onCompleteCallbacks.push(callback);
    return () => {
      this._onCompleteCallbacks = this._onCompleteCallbacks.filter(cb => cb !== callback);
    };
  }

  private _notifyComplete() {
    this._onCompleteCallbacks.forEach(cb => cb());
  }

  constructor() {
    makeObservable(this);

    // Listen for background download progress (URL uploads)
    socketService.on('background-progress', this._onBackgroundProgress);
    socketService.on('background-done', this._onBackgroundDone);
    socketService.on('background-error', this._onBackgroundError);
  }

  /**
   * Upload files selected by the user (chunked upload).
   */
  async uploadFiles(files: FileList | File[], parentFolderId?: string | null): Promise<void> {
    for (const file of Array.from(files)) {
      this._uploadFile(file, parentFolderId);
    }
  }

  /**
   * Upload a pasted image (data URL converted to blob).
   */
  async uploadBlob(blob: Blob, fileName: string, parentFolderId?: string | null): Promise<void> {
    const file = new File([blob], fileName, { type: blob.type });
    await this._uploadFile(file, parentFolderId);
  }

  /**
   * Upload from a URL (server-side background download).
   */
  async uploadFromUrl(url: string, parentFolderId?: string | null): Promise<void> {
    try {
      const result = await apiService.post<{
        id: string;
        fileName: string;
        fileSize: number;
        readySize: number;
        isComplete: boolean;
      }>('/api/file/uploadurl', { url, parentFolderId: parentFolderId ?? null });

      const item: UploadItem = {
        id: result.id,
        fileName: result.fileName,
        fileSize: result.fileSize,
        loaded: result.readySize,
        progress: result.fileSize > 0 ? Math.round(result.readySize / result.fileSize * 100) : 0,
        status: result.isComplete ? 'done' : 'processing',
        parentFolderId: parentFolderId ?? null,
      };

      runInAction(() => {
        this.uploads.unshift(item);
      });

      // Watch for progress via socket.io
      socketService.watchFile(result.id);

    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      toastService.error(message, 'URL upload failed');
      console.error('URL upload error:', message);
    }
  }

  async refreshFile(fileId: string, file: File, parentFolderId?: string | null): Promise<void> {
    const abortController = new AbortController();

    const tempId = 'tmp-' + Date.now() + '-' + Math.random().toString(36).slice(2);
    runInAction(() => {
      this.uploads.unshift({
        id: tempId,
        fileName: file.name,
        fileSize: file.size,
        loaded: 0,
        progress: 0,
        status: 'registering',
        parentFolderId: parentFolderId ?? null,
        abortController,
      });
    });

    let fileEntry: { id: string; fileName: string; fileSize: number };
    try {
      const result = await apiService.put<{
        id: string;
        fileName: string;
        fileSize: number;
        readySize: number;
        isComplete: boolean;
      }>(`/api/file/${fileId}/refresh`, { fileName: file.name, fileSize: file.size });
      fileEntry = { id: result.id, fileName: result.fileName, fileSize: result.fileSize };
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Re-upload failed';
      runInAction(() => {
        const item = this.uploads.find(u => u.id === tempId);
        if (item) {
          item.status = 'error';
          item.error = errorMessage;
        }
      });
      toastService.error(errorMessage, 'Re-upload failed');
      console.error('Failed to reset file for re-upload:', err);
      return;
    }

    const stillPresent = this.uploads.some(u => u.id === tempId);
    if (!stillPresent) return;

    let trackedItem: UploadItem;
    runInAction(() => {
      const item = this.uploads.find(u => u.id === tempId)!;
      item.id = fileEntry.id;
      item.loaded = 0;
      item.progress = 0;
      item.status = 'uploading';
      trackedItem = item;
    });

    try {
      await this._uploadChunked(file, fileEntry, trackedItem!, abortController, 0);
    } catch (err: unknown) {
      if (abortController.signal.aborted) return;
      const errorMessage = err instanceof Error ? err.message : 'Re-upload failed';
      runInAction(() => {
        trackedItem!.status = 'error';
        trackedItem!.error = errorMessage;
      });
      toastService.error(errorMessage, `Re-upload failed: ${trackedItem!.fileName}`);
    }
  }

  /**
   * Cancel an ongoing upload.
   */
  @action
  cancelUpload(uploadId: string) {
    const item = this.uploads.find(u => u.id === uploadId);
    if (item) {
      item.abortController?.abort();
      this.uploads = this.uploads.filter(u => u.id !== uploadId);
      // Skip server delete while still registering — no real ID exists yet.
      // If the registration completes after cancel, _uploadFile will detect
      // the item is gone and clean up the server entry then.
      if (item.status !== 'registering') {
        apiService.delete(`/api/file/${uploadId}/permanent`).catch(err => {
          console.warn('Failed to clean up cancelled upload:', err);
        });
      }
    }
  }

  /**
   * Remove a completed/errored upload from the list.
   */
  @action
  removeUpload(uploadId: string) {
    this.uploads = this.uploads.filter(u => u.id !== uploadId);
  }

  @action
  clearCompleted() {
    this.uploads = this.uploads.filter(u => u.status !== 'done' && u.status !== 'error');
  }

  get hasActiveUploads(): boolean {
    return this.uploads.some(u => u.status === 'registering' || u.status === 'uploading' || u.status === 'processing');
  }

  private async _uploadFile(file: File, parentFolderId?: string | null): Promise<void> {
    const abortController = new AbortController();

    // Show a placeholder immediately so the user sees the item appear without delay
    const tempId = 'tmp-' + Date.now() + '-' + Math.random().toString(36).slice(2);
    runInAction(() => {
      this.uploads.unshift({
        id: tempId,
        fileName: file.name,
        fileSize: file.size,
        loaded: 0,
        progress: 0,
        status: 'registering',
        parentFolderId: parentFolderId ?? null,
        abortController,
      });
    });

    // Step 1: Reuse an incomplete upload when possible
    let fileEntry: { id: string; fileName: string; fileSize: number };
    let initialLoaded = 0;
    try {
      const query = `/api/file/incomplete?fileName=${encodeURIComponent(file.name)}&fileSize=${file.size}`;
      const incomplete = await apiService.get<IncompleteFileInfo | null>(query);

      if (incomplete && incomplete.readySize > 0 && incomplete.readySize < file.size) {
        const progress = Math.round((incomplete.readySize / file.size) * 100);
        const shouldResume = await modalService.showConfirmation(
          `Found an incomplete upload for ${file.name} (${progress}%). Continue from last progress?`,
          'Resume Upload',
          { buttonTitle: 'Resume' }
        );

        if (shouldResume) {
          fileEntry = { id: incomplete.id, fileName: incomplete.fileName, fileSize: incomplete.fileSize };
          initialLoaded = incomplete.readySize;
        } else {
          fileEntry = await apiService.post<typeof fileEntry>('/api/file', {
            fileName: file.name,
            fileSize: file.size,
            url: '',
            parentFolderId: parentFolderId ?? null,
          });
        }
      } else {
        fileEntry = await apiService.post<typeof fileEntry>('/api/file', {
          fileName: file.name,
          fileSize: file.size,
          url: '',
          parentFolderId: parentFolderId ?? null,
        });
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      runInAction(() => {
        const item = this.uploads.find(u => u.id === tempId);
        if (item) {
          item.status = 'error';
          item.error = errorMessage;
        }
      });
      console.error('Failed to prepare upload:', err);
      return;
    }

    // If the item was cancelled while we were registering, clean up the server entry and bail
    const stillPresent = this.uploads.some(u => u.id === tempId);
    if (!stillPresent) {
      apiService.delete(`/api/file/${fileEntry.id}/permanent`).catch(err => {
        console.warn('Failed to clean up cancelled upload:', err);
      });
      return;
    }

    // Upgrade the placeholder to a real upload item
    let trackedItem: UploadItem;
    runInAction(() => {
      const item = this.uploads.find(u => u.id === tempId)!;
      item.id = fileEntry.id;
      item.loaded = initialLoaded;
      item.progress = file.size > 0 ? Math.round((initialLoaded / file.size) * 100) : 0;
      item.status = 'uploading';
      trackedItem = item;
    });

    // Step 2: Upload file in chunks
    try {
      await this._uploadChunked(file, fileEntry, trackedItem!, abortController, initialLoaded);
    } catch (err: unknown) {
      if (abortController.signal.aborted) return;
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      runInAction(() => {
        trackedItem!.status = 'error';
        trackedItem!.error = errorMessage;
      });
      toastService.error(errorMessage, `Upload failed: ${trackedItem!.fileName}`);
    }
  }

  private _uploadChunkWithXhr(
    url: string,
    formData: FormData,
    headers: Record<string, string>,
    signal: AbortSignal,
    onProgress: (loaded: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', url);

      for (const [key, value] of Object.entries(headers)) {
        xhr.setRequestHeader(key, value);
      }

      const abortHandler = () => xhr.abort();
      signal.addEventListener('abort', abortHandler);

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress(e.loaded);
        }
      });

      xhr.addEventListener('load', () => {
        signal.removeEventListener('abort', abortHandler);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Chunk upload failed (${xhr.status}): ${xhr.responseText}`));
        }
      });

      xhr.addEventListener('error', () => {
        signal.removeEventListener('abort', abortHandler);
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        signal.removeEventListener('abort', abortHandler);
        reject(new DOMException('Upload aborted', 'AbortError'));
      });

      xhr.send(formData);
    });
  }

  private async _uploadChunked(
    file: File,
    fileEntry: { id: string; fileName: string; fileSize: number },
    uploadItem: UploadItem,
    abortController: AbortController,
    initialOffset = 0
  ): Promise<void> {
    const totalSize = file.size;
    let offset = initialOffset;
    // Throttle speed/ETA updates to once per second across all chunks
    let lastSpeedUpdate = 0;
    // Accumulate bytes across chunks for a rolling speed window
    const speedWindowStart = { ts: Date.now(), loaded: initialOffset };

    while (offset < totalSize) {
      if (abortController.signal.aborted) {
        throw new DOMException('Upload cancelled', 'AbortError');
      }

      const end = Math.min(offset + CHUNK_SIZE, totalSize);
      const chunkOffset = offset;
      const chunk = file.slice(chunkOffset, end);

      const formData = new FormData();
      formData.append('file', chunk, file.name);
      formData.append('fileName', file.name);
      formData.append('fileSize', totalSize.toString());

      const headers: Record<string, string> = {};
      if (totalSize > CHUNK_SIZE) {
        headers['Content-Range'] = `bytes ${chunkOffset}-${end - 1}/${totalSize}`;
      }

      await this._uploadChunkWithXhr(
        `/api/file/${fileEntry.id}`,
        formData,
        headers,
        abortController.signal,
        (xhrLoaded) => {
          const now = Date.now();
          const totalLoaded = chunkOffset + xhrLoaded;
          // Progress bar updates freely
          runInAction(() => {
            uploadItem.loaded = totalLoaded;
            uploadItem.progress = Math.round(totalLoaded / totalSize * 100);
          });
          // Speed/ETA throttled to once per second
          if (now - lastSpeedUpdate < 1000) return;
          lastSpeedUpdate = now;
          const elapsed = (now - speedWindowStart.ts) / 1000;
          const bytesPerSec = elapsed > 0 ? (totalLoaded - speedWindowStart.loaded) / elapsed : 0;
          const remaining = totalSize - totalLoaded;
          runInAction(() => {
            uploadItem.bitrate = bytesPerSec;
            uploadItem.timeLeftSeconds = bytesPerSec > 0 ? remaining / bytesPerSec : undefined;
          });
        }
      );

      offset = end;
      runInAction(() => {
        uploadItem.loaded = offset;
        uploadItem.progress = Math.round(offset / totalSize * 100);
      });
    }

    // Mark as done
    runInAction(() => {
      uploadItem.status = 'done';
      uploadItem.progress = 100;
      uploadItem.loaded = totalSize;
      uploadItem.abortController = undefined;
    });
    this._notifyComplete();
  }

  @action.bound
  private _onBackgroundProgress(data: BackgroundProgressData) {
    const item = this.uploads.find(u => u.id === data.id);
    if (item) {
      item.loaded = data.loaded;
      item.progress = data.total > 0 ? Math.round(data.loaded / data.total * 100) : 0;
      item.bitrate = data.bitrate ?? undefined;
      item.status = 'processing';
    }
  }

  @action.bound
  private _onBackgroundDone(data: BackgroundDoneData) {
    const item = this.uploads.find(u => u.id === data.id);
    if (item) {
      item.status = 'done';
      item.progress = 100;
      item.loaded = data.fileSize;
    }
    this._notifyComplete();
  }

  @action.bound
  private _onBackgroundError(data: BackgroundErrorData) {
    const item = this.uploads.find(u => u.id === data.id);
    if (item) {
      item.status = 'error';
      item.error = data.error;
    }
  }

  formatBitrate(bytesPerSec: number | undefined): string {
    if (!bytesPerSec) return '';
    const bitsPerSec = bytesPerSec * 8;
    if (bitsPerSec >= 1_000_000) {
      return (bitsPerSec / 1_000_000).toFixed(1) + ' Mbps';
    }
    if (bitsPerSec >= 1_000) {
      return (bitsPerSec / 1_000).toFixed(0) + ' kbps';
    }
    return bitsPerSec.toFixed(0) + ' bps';
  }

  formatTimeLeft(seconds: number | undefined): string {
    if (!seconds || seconds <= 0) return '';
    if (seconds < 60) return Math.ceil(seconds) + 's';
    if (seconds < 3600) return Math.ceil(seconds / 60) + 'min';
    return Math.ceil(seconds / 3600) + 'h';
  }
}

export const uploadService = new UploadService();
